import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import "font-awesome/css/font-awesome.min.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from 'react-redux';
import App from "./App";
import { i18nReady } from './i18n';
import "./index.css";
import { store } from './store';
// import TagManager from "react-gtm-module";
import { initAnalyticsBridge } from './helper/analyticsService';

// 🔐 Get nonce from meta (must be injected by backend)
const nonce =
  document.querySelector('meta[property="csp-nonce"]')?.getAttribute('content') || "";

// ==========================
// 🔥 GLOBAL NONCE ENFORCEMENT (CRITICAL)
// ==========================

// Patch createElement (covers most dynamic injections)
const originalCreateElement = document.createElement;
document.createElement = function (tagName, options) {
  const el = originalCreateElement.call(document, tagName, options);

  if (tagName === "style" || tagName === "script") {
    if (nonce) {
      el.setAttribute("nonce", nonce);
    }
  }

  return el;
};

// Fallback: MutationObserver (catches edge cases like GTM, recaptcha, etc.)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (
        (node.tagName === "STYLE" || node.tagName === "SCRIPT") &&
        !node.nonce
      ) {
        node.setAttribute("nonce", nonce);
      }
    });
  });
});

observer.observe(document.head, { childList: true });

// ==========================
// 🧵 Module Federation remote URLs
// ==========================
// federation's `remotes` config in vite.config.js needs a runtime-resolvable
// URL per remote (its `external: Promise.resolve(window.__mfRemoteUrls.<name>)`
// entries), since each remote's actual host is only known via env vars, not
// at build time. Every remote (dp, snop, snoe, ...) serves its own bundle
// under an absolute "/<alias>/" base in local dev too — see e.g. dpai_frontend
// and snoe_frontend's vite.config, which set `base` to
// `https://localhost:<port>/<alias>/` whenever local dev certs are present —
// so the "/<alias>/" path segment is part of the URL in BOTH dev and prod,
// same as the legacy loader's own manifest-fetch path (MicroFrontend.jsx
// fetches `${host}/{name}/asset-manifest.json`). The previous dev/prod
// asymmetry here (dropping the "/<alias>/" segment only in dev) pointed dev
// at `${host}/assets/remoteEntry.js`, which 404s against every remote's
// actual dev server path.
globalThis.__mfRemoteUrls = {
  dp: `${import.meta.env.VITE_DPAI_HOST}/dp/assets/remoteEntry.js`,
  edm: `${import.meta.env.VITE_EDM_HOST}/edm/assets/remoteEntry.js`,
  snop: `${import.meta.env.VITE_SNOP_HOST}/snop/assets/remoteEntry.js`,
  snoe: `${import.meta.env.VITE_SNOE_HOST}/snoe/assets/remoteEntry.js`,
  ds: `${import.meta.env.VITE_DSAI_HOST}/ds/assets/remoteEntry.js`,
  admin: `${import.meta.env.VITE_ADMIN_HOST}/admin/assets/remoteEntry.js`,
  integration: `${import.meta.env.VITE_INTEGRATION_HOST}/integration/assets/remoteEntry.js`,
  tms: `${import.meta.env.VITE_TMS_HOST}/tms/assets/remoteEntry.js`,
  snp: `${import.meta.env.VITE_SNP_HOST}/snp/assets/remoteEntry.js`,
};

// ==========================
// 🎨 Emotion Cache (MUI fix)
// ==========================
const emotionCache = createCache({
  key: 'csp-nonce',
  nonce: nonce,
  prepend: true,
});

// ==========================
// 📊 Analytics / GTM
// ==========================
// const tagManagerArgs = {
//   gtmId: import.meta.env.VITE_GTM_ID,
// };
//
// // IMPORTANT: initialize AFTER nonce patch
// TagManager.initialize(tagManagerArgs);

initAnalyticsBridge();

const renderWithProviders = (containerId, app, withStrictMode = false) => {
  const root = ReactDOM.createRoot(document.getElementById(containerId));
  const content = (
    <Provider store={store}>
      <CacheProvider value={emotionCache}>
        {app}
      </CacheProvider>
    </Provider>
  );

  root.render(
    withStrictMode ? (
      <React.StrictMode>{content}</React.StrictMode>
    ) : content
  );
};

// ==========================
// ⚛️ React App Render
// ==========================
i18nReady.then(
  () => renderWithProviders("root", <App />, true),
  () => renderWithProviders("root", <App />, true)
);

// ==========================
// 🧩 Micro frontend support
// ==========================
window.rendercreatereactapp = (containerId, history) => {
  i18nReady.then(
    () => renderWithProviders(containerId, <App history={history} />),
    () => renderWithProviders(containerId, <App history={history} />)
  );
};

window.unmountcreatereactapp = (containerId) => {
  const el = document.getElementById(containerId);
  if (el) {
    ReactDOM.createRoot(el).unmount();
  }
};
